/**
 * Medewerker-kostprijs — auto-afleiding (batch-4 `medewerker-kostprijs-autofill`).
 *
 * Pure, I/O-vrije helpers die de auto-gevulde kosten afleiden uit het echte
 * salaris, het gekoppelde voertuig, de uitrusting/credits en de woon-werk-
 * afstand. De service (`apps/api/.../employee-cost-service.ts`) haalt de ruwe
 * DB-data op en voedt die hier aan; zo blijft de logica unit-testbaar zonder DB.
 *
 * "Één waarheid": autokosten volgen het voertuig (niet dubbel op het profiel),
 * brutoloon volgt het salaris. Een handmatige afwijking (met reden) overschrijft
 * de auto-waarde in de service-laag.
 */

import type {
  AutofillParameters,
  BijkomendeCostDerivation,
  VehicleCostDerivation,
} from '@streep/types'

const round2 = (n: number): number => Math.round(n * 100) / 100

/// Vaste afschrijvings-/credit-regels uit de spec. Aanvulbaar via de parameter-
/// set (de service mag deze defaults overschrijven met parameter-waarden).
export const DEFAULT_AUTOFILL_PARAMS: AutofillParameters = {
  werkdagenJaar: 215,
  brandstofPerLiter: 2.15,
  gemVerbruikLiter100km: 8,
  autoAfschrijvingJaren: 5,
  telefoonAfschrijvingMaanden: 60, // 5 jaar
  elektronicaAfschrijvingMaanden: 60, // 5 jaar
  gereedschapAfschrijvingMaanden: 24, // 2 jaar
  kledingCreditPerKwartaal: 350, // -> /3 per maand
}

/// Leasemaatschappijen met een all-in contract (alle kosten in het leasebedrag,
/// excl. boetes/tankpas). Case-insensitief; uitbreidbaar. Wassink is de bekende.
export const ALL_IN_LEASE_MAATSCHAPPIJEN = ['wassink'] as const

/// Herken een all-in leasemaatschappij via (een deel van) de naam.
export function isAllInLeaseMaatschappij(naam: string | null | undefined): boolean {
  if (!naam) return false
  const lower = naam.toLowerCase()
  return ALL_IN_LEASE_MAATSCHAPPIJEN.some((m) => lower.includes(m))
}

/// Subset van de Voertuig-velden die de autokosten-afleiding nodig heeft.
export interface VoertuigCostInput {
  id: string
  kenteken: string | null
  eigendom: string // 'KOOP' | 'FINANCIAL_LEASE' | 'OPERATIONAL_LEASE' | 'HUUR'
  leaseMaatschappij: string | null
  // Lease-/maandbedrag (IC v2 `leaseprijs` > `maandbedragExclBtw` > `maandbedragInclBtw`).
  leaseprijs: number | null
  maandbedragExclBtw: number | null
  maandbedragInclBtw: number | null
  verzekeringPM: number | null
  onderhoudsbudgetPM: number | null
  aanschafBedrag: number | null
  cataloguswaarde: number | null
  bijtelling: boolean | null
  bijtellingPercentage: number | null
  tankpasNummer: string | null
}

/**
 * Leid de maandelijkse autokosten af uit het gekoppelde voertuig (spec deel 2).
 * - All-in lease (Wassink e.d.): alleen het leasebedrag; onderhoud/verzekering/
 *   brandstof zitten in de lease.
 * - Andere lease/huur: leasebedrag + onderhoud/verzekering als losse post.
 * - Koop: geen lease; afschrijving = (aanschaf - restwaarde)/jaren/12 + losse
 *   onderhoud/verzekering.
 * Brandstof is "inbegrepen" (geen aparte post) bij all-in lease of een tankpas.
 */
export function deriveVehicleCost(
  v: VoertuigCostInput,
  opts: { autoAfschrijvingJaren: number; restwaardePct?: number } = {
    autoAfschrijvingJaren: DEFAULT_AUTOFILL_PARAMS.autoAfschrijvingJaren,
  },
): VehicleCostDerivation {
  const isAllIn = isAllInLeaseMaatschappij(v.leaseMaatschappij)
  const isKoop = v.eigendom === 'KOOP'
  const heeftTankpas = Boolean(v.tankpasNummer)
  const leaseAmount = v.leaseprijs ?? v.maandbedragExclBtw ?? v.maandbedragInclBtw ?? 0
  const verzekering = v.verzekeringPM ?? 0
  const onderhoud = v.onderhoudsbudgetPM ?? 0

  let leaseBedragMaand = 0
  let afschrijvingMaand = 0
  let verzekeringMaand = 0
  let onderhoudMaand = 0
  let brandstofInbegrepen = false

  if (isKoop) {
    // Eigendom -> afschrijving, geen lease.
    const jaren = opts.autoAfschrijvingJaren > 0 ? opts.autoAfschrijvingJaren : 5
    const aanschaf = v.aanschafBedrag ?? v.cataloguswaarde ?? 0
    const restwaarde = aanschaf * ((opts.restwaardePct ?? 0) / 100)
    afschrijvingMaand = aanschaf > 0 ? (aanschaf - restwaarde) / jaren / 12 : 0
    verzekeringMaand = verzekering
    onderhoudMaand = onderhoud
    brandstofInbegrepen = heeftTankpas
  } else if (isAllIn) {
    // All-in lease: alleen het leasebedrag (rest zit in de lease).
    leaseBedragMaand = leaseAmount
    brandstofInbegrepen = true
  } else {
    // Andere lease/huur: leasebedrag + onderhoud/verzekering los.
    leaseBedragMaand = leaseAmount
    verzekeringMaand = verzekering
    onderhoudMaand = onderhoud
    brandstofInbegrepen = heeftTankpas
  }

  return {
    voertuigId: v.id,
    kenteken: v.kenteken,
    leaseMaatschappij: v.leaseMaatschappij,
    isAllInLease: isAllIn,
    isKoop,
    heeftTankpas,
    bijtelling: v.bijtelling,
    bijtellingPercentage: v.bijtellingPercentage,
    leaseBedragMaand: round2(leaseBedragMaand),
    verzekeringMaand: round2(verzekeringMaand),
    onderhoudMaand: round2(onderhoudMaand),
    afschrijvingMaand: round2(afschrijvingMaand),
    brandstofInbegrepen,
  }
}

/**
 * Brandstofkosten woon-werk per maand (spec deel 4): retour-afstand x werkdagen
 * x brandstofkosten/km. Brandstofkosten/km = literprijs x verbruik/100km / 100.
 * 0 wanneer brandstof al in de lease/tankpas zit (`brandstofInbegrepen`).
 */
export function deriveFuelCostMaand(input: {
  woonWerkAfstandKm: number | null
  werkdagenJaar: number
  brandstofPerLiter: number
  gemVerbruikLiter100km: number
  brandstofInbegrepen: boolean
}): number {
  if (input.brandstofInbegrepen) return 0
  const afstand = input.woonWerkAfstandKm ?? 0
  if (afstand <= 0) return 0
  const kostenPerKm = (input.brandstofPerLiter * input.gemVerbruikLiter100km) / 100
  const retourPerDag = afstand * 2
  const jaarKosten = retourPerDag * input.werkdagenJaar * kostenPerKm
  return round2(jaarKosten / 12)
}

/**
 * Bijkomende kosten per maand (spec deel 3): telefoon/elektronica = waarde/60,
 * gereedschap = waarde/24, kleding = credit/kwartaal / 3. Aanschafwaarden uit de
 * uitrusting/voorraad waar bekend; null => 0 (handmatig aanvullen). Telefoon
 * telt daarnaast het maandbedrag van de lopende abonnementen op naam.
 */
export function deriveBijkomendeCost(
  input: {
    telefoonWaarde: number | null
    elektronicaWaarde: number | null
    gereedschapWaarde: number | null
    kledingCreditPerKwartaal: number | null
    /** Som van de lopende telefoon-/data-abonnementen (excl. btw, per maand). */
    telefoonAbonnementMaand?: number | null
  },
  params: Pick<
    AutofillParameters,
    | 'telefoonAfschrijvingMaanden'
    | 'elektronicaAfschrijvingMaanden'
    | 'gereedschapAfschrijvingMaanden'
    | 'kledingCreditPerKwartaal'
  > = DEFAULT_AUTOFILL_PARAMS,
): BijkomendeCostDerivation {
  const tMnd = params.telefoonAfschrijvingMaanden > 0 ? params.telefoonAfschrijvingMaanden : 60
  const eMnd = params.elektronicaAfschrijvingMaanden > 0 ? params.elektronicaAfschrijvingMaanden : 60
  const gMnd = params.gereedschapAfschrijvingMaanden > 0 ? params.gereedschapAfschrijvingMaanden : 24
  const kledingCredit = input.kledingCreditPerKwartaal ?? params.kledingCreditPerKwartaal

  return {
    telefoonMaand: round2(
      (input.telefoonWaarde != null ? input.telefoonWaarde / tMnd : 0) +
        (input.telefoonAbonnementMaand ?? 0),
    ),
    elektronicaMaand: input.elektronicaWaarde != null ? round2(input.elektronicaWaarde / eMnd) : 0,
    gereedschapMaand: input.gereedschapWaarde != null ? round2(input.gereedschapWaarde / gMnd) : 0,
    // Kleding via credits: 350/kwartaal = /3 per maand (≈116,67 bij de standaard
    // 350; de spec rondt dit naar 117). Eén waarheid = het credit-bedrag /3.
    kledingMaand: round2(kledingCredit / 3),
    telefoonWaarde: input.telefoonWaarde,
    elektronicaWaarde: input.elektronicaWaarde,
    gereedschapWaarde: input.gereedschapWaarde,
    telefoonAbonnementMaand: input.telefoonAbonnementMaand ?? null,
  }
}
